import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button, cn } from '@llura/ui';

import { useStrings } from '../../hooks/useStrings';

export type IntroStepNumber = 1 | 2 | 3 | 4 | 5;

const TOTAL_STEPS = 5;

interface IntroScreenProps {
  step: IntroStepNumber;
  /** Small uppercase eyebrow tag (only on screens 2-4 per spec). */
  tag?: string;
  title: string;
  italic: string;
  /** Optional aura/visualisation rendered above the body. */
  visual?: ReactNode;
  /** Main body text under the title block. */
  body: string;
  /** Any custom content rendered between the body and the action row (e.g. safe-word UI). */
  extra?: ReactNode;
  onBack?: () => void;
  onNext?: () => void;
  /** When set, shows a discreet "Sign in" link in the top-right (start screen only). */
  onLogin?: () => void;
  /** Override the primary CTA label. Default is `intro.nextCta` ("Weiter"). */
  nextLabel?: string;
  /** Tone of the primary CTA. `emerald` matches the safe-space (cocoon) mood on screen 4. */
  nextTone?: 'primary' | 'emerald';
  /** Optional secondary CTA rendered under the primary (e.g. "Ohne Safe Word fortfahren"). */
  secondaryCta?: ReactNode;
}

/**
 * Shared chrome for all four intro screens: progress dots up top, centred
 * content stack in the middle, action row + persistent discretion footer
 * at the bottom. Background gradients keep the boudoir mood consistent.
 */
export function IntroScreen({
  step,
  tag,
  title,
  italic,
  visual,
  body,
  extra,
  onBack,
  onNext,
  onLogin,
  nextLabel,
  nextTone = 'primary',
  secondaryCta,
}: IntroScreenProps) {
  const { strings } = useStrings();

  return (
    <div className="relative flex min-h-[100svh] flex-col overflow-hidden bg-background px-5">
      <BackgroundBlooms step={step} />

      {onLogin && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onLogin}
          className="absolute right-4 top-5 z-20 h-8 rounded-full px-3 text-xs text-muted-foreground hover:text-foreground"
        >
          {strings.intro.loginCta}
        </Button>
      )}

      <header className="relative z-10 flex items-center pt-5">
        <div className="w-10">
          {onBack && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              aria-label={strings.intro.backAria}
              className="rounded-full"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
          )}
        </div>
        <ProgressDots current={step} total={TOTAL_STEPS} className="flex-1 justify-center" />
        <div className="w-10" />
      </header>

      <motion.main
        key={step}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
        className="relative z-10 mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center gap-5 py-6 text-center"
      >
        {visual && <div className="my-1">{visual}</div>}

        {tag && (
          <p className="text-[10px] uppercase tracking-[0.4em] text-accent">{tag}</p>
        )}

        <h1 className="font-serif text-4xl leading-tight tracking-tight text-foreground sm:text-5xl">
          {title}
        </h1>

        <p className="font-serif italic text-lg leading-relaxed text-muted-foreground sm:text-xl">
          {italic}
        </p>

        <p className="max-w-md text-balance text-sm leading-relaxed text-foreground/85 sm:text-base">
          {body}
        </p>

        {extra && <div className="w-full pt-2">{extra}</div>}
      </motion.main>

      <footer className="relative z-10 mx-auto w-full max-w-md space-y-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
        {onNext && (
          <Button
            onClick={onNext}
            size="lg"
            className={cn(
              'h-14 w-full rounded-full',
              nextTone === 'emerald'
                ? 'bg-emerald-500 text-white hover:bg-emerald-500/90 shadow-[0_0_36px_rgba(52,211,153,0.45),0_0_80px_rgba(52,211,153,0.18)]'
                : 'bg-primary text-primary-foreground glow-primary',
            )}
          >
            {nextLabel ?? strings.intro.nextCta}
            {!nextLabel && <ChevronRight className="ml-1 h-4 w-4" />}
          </Button>
        )}

        {secondaryCta}

        <p className="text-center text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {strings.intro.discretionFooter}
        </p>
      </footer>
    </div>
  );
}

function ProgressDots({
  current,
  total,
  className,
}: {
  current: number;
  total: number;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
        <span
          key={n}
          aria-hidden
          className={cn(
            'h-1.5 rounded-full transition-all',
            n === current
              ? 'w-6 bg-primary'
              : n < current
                ? 'w-1.5 bg-primary/60'
                : 'w-1.5 bg-muted-foreground/30',
          )}
        />
      ))}
      <span className="sr-only">
        Step {current} of {total}
      </span>
    </div>
  );
}

function BackgroundBlooms({ step }: { step: IntroStepNumber }) {
  // Screens 1-4 share the plum + rose-gold ambient; the pact screen (5) adds a
  // soft emerald breath so the safety mood reads even without the central aura.
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-primary/12 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 right-[-10%] h-[420px] w-[420px] rounded-full bg-secondary/12 blur-3xl"
      />
      {step === 5 && (
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-[10%] left-[-10%] h-[320px] w-[320px] rounded-full bg-emerald-400/10 blur-3xl"
        />
      )}
    </>
  );
}
